import { BrandfolderHexColors, useHover } from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, ReactNode, useContext, useRef } from 'react';
import styled from 'styled-components';

import { DragAndDropContext } from '@context/drag-and-drop';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';
import { ThemeContext } from '@context/theme';
import { BlockTypes } from '@enums/blocks';
import { Block } from '@typings/block';

import { BlockEditorButtons } from './block-editor-buttons';

interface BlockEditorProps extends HTMLAttributes<HTMLDivElement> {
  block: Block;
  children: ReactNode;
  index: number;
  edit?: boolean;
  first?: boolean;
  last?: boolean;
  only?: boolean;
  setEdit?: (edit: boolean) => void;
}

const UnstyledBlockEditor: FunctionComponent<BlockEditorProps> = (props) => {
  const { block, children, edit, first, index, only, last, setEdit, ...otherProps } = props;
  const { parentKey, type } = block;

  const dragImageRef = useRef<HTMLDivElement | null>(null);

  const { hovered, hoverProps } = useHover();

  const { dragging } = useContext(DragAndDropContext);
  const { editable } = useContext(PermissionLevelContext);
  const { preview } = useContext(PreviewContext);
  const { isLight, theme } = useContext(ThemeContext);

  const editing = editable && !preview;

  if (!editing) return null;

  const color = type === BlockTypes.Color;
  const section = type === BlockTypes.Section;
  const text = type === BlockTypes.Text;

  const sectionHeading = !!parentKey && index === 0 && text;

  return (
    <div
      {...otherProps}
      {...hoverProps}
      className={classNames({
        'block-editor': true,
        'block-editor--color': color,
        'block-editor--dark': !isLight,
        'block-editor--dragging': dragging,
        'block-editor--hovered': hovered,
        'block-editor--section': section,
        'block-editor--section-heading': sectionHeading,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      data-testid="block-editor"
      ref={dragImageRef}
    >
      <BlockEditorButtons
        block={block}
        dragImageRef={dragImageRef}
        edit={edit}
        first={first}
        last={last}
        only={only}
        sectionHeading={sectionHeading}
        setEdit={setEdit}
      />
      <div className="block-editor__block" style={{ backgroundColor: theme.body.backgroundColor }}>
        {children}
      </div>
    </div>
  );
};

const StyledBlockEditor = styled(UnstyledBlockEditor)`
  display: flex;
  flex-direction: column;
  margin: 0;
  position: relative;

  &:not(.block-editor--color) {
    clear: both;
  }

  .block-editor__block {
    border: 1px solid transparent;
    padding: 4px;
  }

  &.block-editor--hovered,
  &:hover {
    > .block-editor__block {
      border-color: ${BrandfolderHexColors.Gray};
    }
  }

  &.block-editor--color {
    float: left;
    height: 330px;
    margin: 4px 4px 0 0;

    .bf-color-swatch {
      margin: 0;
    }

    + .canvas-drop--color {
      float: left;
      height: 324px;
      margin: 0 8px;
      width: 0;

      &.canvas-drop--over {
        width: 40px;
      }

      .canvas-drop__text {
        padding: 12px 0;
        text-orientation: upright;
        writing-mode: vertical-rl;
      }
    }
  }

  .image,
  .text {
    margin: 0;
  }

  &.block-editor--section {
    .block-editor-buttons--section {
      align-items: center;
      border: 1px solid transparent;
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
      justify-content: center;
      margin: 0 auto;
      padding: 4px 10px 4px 16px;
      top: 1px;
      width: 180px;

      .bf-heading__standard {
        font-size: 12px;
        line-height: 18px;
        margin-right: auto;
        text-transform: uppercase;
      }

      .bf-button__icon {
        border: 0;
        height: 24px;
        margin-left: 4px;
        padding: 0;
        width: 24px;

        > .bf-icon__font {
          font-size: 10px;
        }
      }

      .block-editor-buttons__drag {
        > .bf-icon__font {
          font-size: 14px;
        }
      }
    }

    &.block-editor--dragging,
    &.block-editor--hovered,
    &:hover {
      .block-editor-buttons--section {
        border-color: ${BrandfolderHexColors.Gray};
      }
    }

    .mce-content-body {
      margin: 0;
    }
  }
`;

export const BlockEditor: FunctionComponent<BlockEditorProps> = (props) => {
  return <StyledBlockEditor {...props} />;
};
